import { useMediaQuery, useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import isString from 'lodash/isString';
import { Breakpoint } from '@mui/system';
import { getLocalStorage, LOCAL_STORAGE_NAMES } from '@libs/storage/src/lib/local';
import { TLocalizedNameObject } from '@crema/models/masterProduct';

export const useWidth = () => {
  const theme = useTheme();
  const keys = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output, key: any) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || 'xs'
  );
};
export const getBreakPointsValue = (valueSet: any, breakpoint: Breakpoint) => {
  if (typeof valueSet === 'number') return valueSet;
  switch (breakpoint) {
    case 'xs':
      return valueSet.xs;
    case 'sm':
      return valueSet.sm || valueSet.xs;
    case 'md':
      return valueSet.md || valueSet.sm || valueSet.xs;
    case 'lg':
      return valueSet.lg || valueSet.md || valueSet.sm || valueSet.xs;
    default:
      return valueSet.xl || valueSet.lg || valueSet.md || valueSet.sm || valueSet.xs;
  }
};

export const getFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024,
    dm = 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const multiPropsFilter = (products: any[], filters: any, stringKey = 'title') => {
  const filterKeys = Object.keys(filters);
  return products.filter((product) => {
    return filterKeys.every((key) => {
      if (!filters[key].length) return true;
      // Loops again if product[key] is an array (for material attribute).
      if (Array.isArray(product[key])) {
        return product[key].some((keyEle: any) => filters[key].includes(keyEle));
      }
      if (key === stringKey) {
        return product[key].toLowerCase().includes(filters[key].toLowerCase());
      }
      return filters[key].includes(product[key]);
    });
  });
};

// 'intl' service singleton reference
let intl: IntlShape;
export const IntlGlobalProvider = ({ children }: { children: ReactNode }): any => {
  intl = useIntl();
  // Keep the 'intl' service reference
  return children;
};

export const appIntl = () => {
  return intl;
};

export const isValidString = (str: string) => {
  return isString(str) && str.length > 1;
};

export const getValueByLangKey = (localizedNames: Array<any>) => {
  const lang = getLocalStorage(LOCAL_STORAGE_NAMES.LANG);

  const localizedName = localizedNames.find((item: any) => item.langCode === lang?.toUpperCase());
  // console.log('localizedName', localizedNames);

  return localizedName?.name || localizedNames[0].name;
};

export const getLocalizedValueByLangKey = (localizedValues: Array<any>) => {
  const lang = getLocalStorage(LOCAL_STORAGE_NAMES.LANG);
  return localizedValues?.find((item: any) => item.langCode === lang?.toUpperCase());
};

export const getLocalizedValueFromObject = (localizedValues: TLocalizedNameObject) => {
  const lang = (getLocalStorage(LOCAL_STORAGE_NAMES.LANG) as keyof TLocalizedNameObject) || 'KO';

  return localizedValues[lang];
};
